import React from 'react';
import { ToastItem, ToastOptions, ToastType } from './toast-types';

type Listener = (toasts: ToastItem[]) => void;

class ToastStore {
  private toasts: ToastItem[] = [];
  private listeners: Set<Listener> = new Set();
  private count = 0;

  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.toasts);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach((listener) => listener([...this.toasts]));
  }

  public getToasts(): ToastItem[] {
    return [...this.toasts];
  }

  public add(
    message: React.ReactNode,
    options?: ToastOptions,
    type: ToastType = 'default',
  ): string | number {
    const id = options?.id ?? `toast_${Date.now()}_${++this.count}`;

    // Normalize duration
    let duration = options?.duration;
    if (options?.autoClose !== undefined) {
      if (options.autoClose === false) {
        duration = Infinity;
      } else if (typeof options.autoClose === 'number') {
        duration = options.autoClose;
      }
    }
    if (duration === undefined) {
      duration = 4000;
    }

    const toastItem: ToastItem = {
      ...options,
      id,
      message,
      type: options?.type ?? type,
      duration,
      createdAt: Date.now(),
      dismissible: options?.dismissible !== false,
      position: 'top-right',
    };

    // Replace if id already exists, otherwise add to list
    const existingIndex = this.toasts.findIndex((t) => t.id === id);
    if (existingIndex !== -1) {
      this.toasts[existingIndex] = toastItem;
    } else {
      // Keep up to 6 toasts active
      if (this.toasts.length >= 6) {
        this.toasts.shift();
      }
      this.toasts.push(toastItem);
    }

    this.notify();
    return id;
  }

  public dismiss(id?: string | number) {
    if (id !== undefined) {
      this.toasts = this.toasts.filter((t) => t.id !== id);
    } else {
      this.toasts = [];
    }
    this.notify();
  }

  public update(id: string | number, options: Partial<ToastItem>) {
    this.toasts = this.toasts.map((t) => {
      if (t.id === id) {
        return { ...t, ...options };
      }
      return t;
    });
    this.notify();
  }

  public isActive(id: string | number): boolean {
    return this.toasts.some((t) => t.id === id);
  }

  public clearWaitingQueue() {
    // No-op for compatibility
  }
}

export const toastStore = new ToastStore();

// Main callable toast function
export const toast: any = (message: React.ReactNode, options?: ToastOptions) => {
  return toastStore.add(message, options, options?.type || 'default');
};

toast.success = (message: React.ReactNode, options?: ToastOptions) => {
  return toastStore.add(message, options, 'success');
};

toast.error = (message: React.ReactNode, options?: ToastOptions) => {
  return toastStore.add(message, options, 'error');
};

toast.info = (message: React.ReactNode, options?: ToastOptions) => {
  return toastStore.add(message, options, 'info');
};

toast.warning = (message: React.ReactNode, options?: ToastOptions) => {
  return toastStore.add(message, options, 'warning');
};

toast.warn = toast.warning;

toast.dismiss = (id?: string | number) => {
  toastStore.dismiss(id);
};

toast.update = (id: string | number, options: Partial<ToastItem>) => {
  toastStore.update(id, options);
};

toast.isActive = (id: string | number) => {
  return toastStore.isActive(id);
};

toast.clearWaitingQueue = () => {
  toastStore.clearWaitingQueue();
};

toast.promise = async <T,>(
  promise: Promise<T>,
  msgs: {
    pending?: React.ReactNode;
    success?: React.ReactNode | ((data: T) => React.ReactNode);
    error?: React.ReactNode | ((error: any) => React.ReactNode);
  },
  options?: ToastOptions,
) => {
  const id = toast(msgs.pending || 'Loading...', {
    ...options,
    type: 'default',
    duration: Infinity,
  });

  try {
    const data = await promise;
    const successMsg =
      typeof msgs.success === 'function' ? msgs.success(data) : msgs.success;
    toast.success(successMsg || 'Action successful', {
      ...options,
      id,
      duration: options?.duration ?? 4000,
    });
    return data;
  } catch (err) {
    const errorMsg =
      typeof msgs.error === 'function' ? msgs.error(err) : msgs.error;
    toast.error(errorMsg || 'An error occurred', {
      ...options,
      id,
      duration: options?.duration ?? 5000,
    });
    throw err;
  }
};

// NotificationManager compatibility helper
export const NotificationManager = {
  success: (
    message: string,
    title?: string,
    timeOut?: number,
    onClick?: () => void,
  ) => {
    return toast.success(message, {
      title,
      duration: timeOut,
      onClick,
    });
  },
  error: (
    message: string,
    title?: string,
    timeOut?: number,
    onClick?: () => void,
  ) => {
    return toast.error(message, {
      title,
      duration: timeOut,
      onClick,
    });
  },
  info: (
    message: string,
    title?: string,
    timeOut?: number,
    onClick?: () => void,
  ) => {
    return toast.info(message, {
      title,
      duration: timeOut,
      onClick,
    });
  },
  warning: (
    message: string,
    title?: string,
    timeOut?: number,
    onClick?: () => void,
  ) => {
    return toast.warning(message, {
      title,
      duration: timeOut,
      onClick,
    });
  },
};
